'use client'

import { logger } from '@carbonara/logger'
import {
	type Language,
	type Locale,
	type Replacements,
	type TranslationKey,
	createIntlShape,
	flattenObject,
} from '@carbonara/translations'
import type { Dictionary } from '@carbonara/translations/src/types'
import type { CountryIsoCode } from '@carbonara/utils'
import { type ReactNode, createContext, useContext, useMemo } from 'react'

interface I18NContext {
	language: Language
	dictionary: Dictionary
	locale: Locale
	getTranslation: (
		key: TranslationKey,
		values?: Replacements,
		languageCode?: Language,
	) => string
	translationOrFallback: (nb: string | null, en: string | null) => string
}

const I18NContext = createContext<I18NContext | undefined>(undefined)

interface ProviderProps {
	language: Language
	dictionary: Dictionary
	children: ReactNode
}

export const I18NContextProvider = ({
	language,
	dictionary,
	children,
}: ProviderProps) => {
	const locale = useMemo(
		() => flattenObject(dictionary) as Locale,
		[dictionary],
	)

	const intl = useMemo(
		() => createIntlShape(language, locale),
		[language, locale],
	)

	const getTranslation = (
		key: TranslationKey,
		values: Parameters<typeof intl.formatMessage>[1] = {},
		languageCode: Language = 'nb',
	) => {
		try {
			const message = intl.formatMessage({ id: key }, values)
			// This message might be an array of strings / elements etc.
			// But we often use this to produce strings to be used as props for components
			return message as string
		} catch (error) {
			logger.error(`Missing translation for ${languageCode}.${key}`)
			return `Missing translation for ${languageCode}.${key}`
		}
	}

	const getCountries = () => dictionary.countries

	const getCountryName = (country: CountryIsoCode) =>
		getTranslation(`countries.${country}`)

	const getCurrencies = () => {
		return dictionary.currencies
	}

	const getFuelTypes = () => {
		return dictionary.fuelTypes
	}

	const getVehicleTypes = () => {
		return dictionary.vehicleTypes
	}

	const translationOrFallback = (nb: string | null, en: string | null) => {
		const text = language === 'nb' ? nb : en
		return text ?? ''
	}

	return (
		<I18NContext.Provider
			value={{
				language,
				locale,
				dictionary,
				getTranslation,
				translationOrFallback,
			}}
		>
			{children}
		</I18NContext.Provider>
	)
}

export const useI18N = (): I18NContext => {
	const context = useContext(I18NContext)

	if (context === undefined) {
		throw new Error('useI18N must be used within a I18NContextProvider')
	}

	return context
}
